import { Modal } from "flowbite-react";
import { IVehicle, VEHICLE_STATUS } from "../../../../types/vehicle.type";
import { useEffect, useState } from "react";
import { vehicleLogsFindByLincesePlate } from "@/common/services/logs.api";
import { getStatusLabelsVehicle } from "../TableVehicles";
import Loading from "@/common/components/ui/loading/Loading";

type Props = {
  vehicle: Partial<IVehicle>;
  onClose: () => void;
};

interface IVehicleLog {
  durationSinceLastStatus: undefined;
  timestamp: Date | string;
  meta: {
    status: VEHICLE_STATUS;
    previousStatus: VEHICLE_STATUS;
    previousTimestamp?: string | Date;
    author?: string;
  };
  accumulatedDays?: number;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const formatLogs = (logs: IVehicleLog[]): IVehicleLog[] => {
  if (!logs?.length) return [];

  const ascending = [...logs].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );

  let lastRentedTs: number | null = null;
  const computed: number[] = new Array(ascending.length).fill(0);

  for (let i = 0; i < ascending.length; i++) {
    const curr = ascending[i];
    const currTs = new Date(curr.timestamp).getTime();
    const isRented = String(curr.meta.status) === "rented";
    const prevIsRented = String(curr.meta.previousStatus) === "rented";

    if (isRented) {
      computed[i] = 0;
      lastRentedTs = currTs;
    } else if (prevIsRented) {
      computed[i] = 0;
      lastRentedTs = currTs;
    } else {
      const diff =
        lastRentedTs == null ? 0 : Math.max(0, currTs - lastRentedTs);
      computed[i] = Math.round(diff / MS_PER_DAY);
    }
  }

  const seen = new Map<number, number>();
  return logs.map((log) => {
    const ts = new Date(log.timestamp).getTime();
    const count = seen.get(ts) ?? 0;
    seen.set(ts, count + 1);

    let mappedI = -1,
      occ = 0;
    for (let i = 0; i < ascending.length; i++) {
      if (new Date(ascending[i].timestamp).getTime() === ts) {
        if (occ === count) {
          mappedI = i;
          break;
        }
        occ++;
      }
    }

    return {
      ...log,
      timestamp: new Date(ts).toISOString(),
      accumulatedDays: mappedI >= 0 ? computed[mappedI] : 0,
    };
  });
};

export default function ModalInfoDaysAtBase(props: Props) {
  const [valueVehicleLogs, setValueVehicleLog] = useState<IVehicleLog[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  useEffect(() => {
    async function loadVehicles(licensePlate: string) {
      if (!licensePlate) return;

      setIsLoading(true);
      try {
        const result = await vehicleLogsFindByLincesePlate(licensePlate);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const normalizedLogs = result.data.map((log: any) => ({
          ...log,
          meta: {
            ...log.meta,
            status: log.meta.status as VEHICLE_STATUS,
            previousStatus: log.meta.previousStatus as VEHICLE_STATUS,
          },
        }));

        setValueVehicleLog(formatLogs(normalizedLogs));
      } catch (error) {
        console.error("Erro ao buscar logs do veículo:", error);
        setValueVehicleLog([]);
      } finally {
        setIsLoading(false);
      }
    }

    if (props.vehicle.licensePlate) {
      loadVehicles(props.vehicle.licensePlate);
    } else {
      setIsLoading(false);
    }
  }, [props.vehicle]);

  const handleCloseModal = () => {
    props.onClose();
  };

  return (
    <Modal
      size={"5xl"}
      className="relative"
      show={true}
      onClose={() => handleCloseModal()}
    >
      <Modal.Header className="text-black">
        <h2 className="dark:text-black">
          Informações do veículo: {props.vehicle.licensePlate}
        </h2>
      </Modal.Header>
      <Modal.Body className="text-black">
        <div className="generic-table">
          <table className="generic-table-style">
            <thead className="generic-table-thead">
              <tr>
                <th className="generic-table-header">Data/Hora do Registro</th>
                <th className="generic-table-header">Status Anterior</th>
                <th className="generic-table-header">Novo Status</th>
                <th className="generic-table-header">
                  Tempo no Status Anterior
                </th>
                <th className="generic-table-header">Tempo na Base</th>
                <th className="generic-table-header">
                  Responsável pela Alteração
                </th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="py-3 text-center">
                    <Loading />
                  </td>
                </tr>
              ) : valueVehicleLogs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-3 text-center">
                    Nenhum registro encontrado.
                  </td>
                </tr>
              ) : (
                valueVehicleLogs.map((item, index) => (
                  <tr
                    key={index}
                    className={`${index % 2 ? "bg-gray2/10" : "bg-white"}`}
                  >
                    <td className="generic-table-th">
                      {item.timestamp
                        ? new Date(item.timestamp).toLocaleString("pt-BR", {
                            timeZone: "America/Sao_Paulo",
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            second: "2-digit",
                          })
                        : "Data não disponível"}
                    </td>
                    <td className="generic-table-th">
                      {getStatusLabelsVehicle(item.meta.previousStatus) ||
                        "Status não disponível"}
                    </td>
                    <td className="generic-table-th">
                      {getStatusLabelsVehicle(item.meta.status) ||
                        "Status não disponível"}
                    </td>
                    <td className="generic-table-th">
                      {item.durationSinceLastStatus !== undefined
                        ? item.durationSinceLastStatus
                        : "Informação não disponível"}
                    </td>
                    <td className="generic-table-th">
                      {item.accumulatedDays !== undefined
                        ? Math.floor(item.accumulatedDays)
                        : "Status não disponível"}
                    </td>
                    <td className="generic-table-th">
                      {item.meta.author || "Status não disponível"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Modal.Body>
    </Modal>
  );
}
